<script setup lang="ts">
const profileStore = useProfileStore();

const show = computed(
  () =>
    profileStore.showUrgentAnnouncementsOnly ||
    profileStore.profile?.displaySSBoostBasicToAdvanced ||
    profileStore.profile?.displaySSBoostAdvancedToPremium ||
    profileStore.profile?.displayLyricsBoost,
);
</script>

<template content>
  <div
    id="above-banner-messages"
    v-if="show"
  >
    <template v-if="profileStore.showUrgentAnnouncementsOnly">
      <Alert
        v-if="profileStore.showExpiredMessage"
        variant="error"
      >
        <i18n-t
          keypath="SV.MES_SubscriptionExpired"
          scope="global"
          tag="span"
        >
          <template v-slot:online>
            <a
              target="_blank"
              class="link"
              :href="
                profileStore.profile?.renewSubscriptionUrl ||
                'https://store.ccli.com'
              "
              >{{ $t("SV.LIT_SubscriptionOnline") }}</a
            >
          </template>
          <template v-slot:phone>
            <a
              target="_blank"
              class="link"
              href="https://ccli.com/contact-ccli"
              >{{ $t("SV.LIT_SubscriptionPhone") }}</a
            >
          </template>
        </i18n-t>
        <template #footer>
          <Button
            id="SubscriptionExpiredRenewCTAButton"
            variant="transparent-white"
            small
            icon-end="fa-light fa-arrow-right"
            :href="
              profileStore.profile?.renewSubscriptionUrl ||
              'https://store.ccli.com'
            "
          >
            {{ $t("G.LIT_Renew") }}
          </Button>
        </template>
      </Alert>
      <Alert
        v-if="profileStore.showExpiringSoonMessage"
        variant="warning"
        icon="fa-light fa-circle-exclamation"
        class="mb-3"
      >
        <i18n-t
          keypath="SV.MES_SubscriptionExpiringSoon"
          scope="global"
          tag="span"
        >
          <template v-slot:online>
            <a
              target="_blank"
              class="link"
              :href="
                profileStore.profile?.renewSubscriptionUrl ||
                'https://store.ccli.com'
              "
              >{{ $t("SV.LIT_SubscriptionOnline") }}</a
            >
          </template>
          <template v-slot:phone>
            <a
              target="_blank"
              class="link"
              href="https://ccli.com/contact-ccli"
              >{{ $t("SV.LIT_SubscriptionPhone") }}</a
            >
          </template>
        </i18n-t>
        <template #footer>
          <Button
            id="SubscriptionExpiringSoonRenewCTAButton"
            variant="transparent-white"
            icon-end="fa-light fa-arrow-right"
            small
            :href="
              profileStore.profile?.renewSubscriptionUrl ||
              'https://store.ccli.com'
            "
          >
            {{ $t("G.LIT_Renew") }}
          </Button>
        </template>
      </Alert>
      <Alert
        v-if="profileStore.showProfileNeedsAttentionMessage"
        variant="warning"
        class="mb-3"
      >
        {{ $t("G.LIT_ProfileRequiresYourAttention") }}
        <template #footer>
          <Button
            id="ProfileAttentionCTAButton"
            variant="transparent-white"
            icon-end="fa-light fa-arrow-right"
            small
            :href="profileStore.profileAttentionUrl"
          >
            {{ $t("SV.LIT_GoToProfile") }}
          </Button>
        </template>
      </Alert>
    </template>
    <template v-else>
      <Alert
        v-if="profileStore.profile?.displaySSBoostBasicToAdvanced"
        variant="info"
      >
        <i18n-t
          keypath="SV.MES_BoostExpiring"
          scope="global"
          tag="span"
        >
          <template v-slot:level>
            <span class="ss-announcement-emphasized">{{
              $t("SV.LIT_SongSelectAdvanced")
            }}</span>
          </template>
          <template v-slot:action>
            <a
              target="_blank"
              class="link"
              :href="
                profileStore.profile?.upgradeSubscriptionUrl ||
                'https://store.ccli.com'
              "
              >{{ $t("SV.LIT_BoostUpgradeYourSubscription") }}</a
            >
          </template>
        </i18n-t>
      </Alert>
      <Alert
        v-if="profileStore.profile?.displaySSBoostAdvancedToPremium"
        variant="info"
      >
        <i18n-t
          keypath="SV.MES_BoostExpiring"
          scope="global"
          tag="span"
        >
          <template v-slot:level>
            <span class="ss-announcement-emphasized">{{
              $t("SV.LIT_SongSelectPremium")
            }}</span>
          </template>
          <template v-slot:action>
            <a
              target="_blank"
              :href="
                profileStore.profile?.upgradeSubscriptionUrl ||
                'https://store.ccli.com'
              "
              class="link"
              >{{ $t("SV.LIT_BoostUpgradeYourSubscription") }}</a
            >
          </template>
        </i18n-t>
      </Alert>
      <Alert
        v-if="profileStore.profile?.displayLyricsBoost"
        variant="info"
      >
        <i18n-t
          keypath="SV.MES_Boost"
          scope="global"
          tag="span"
        >
          <template v-slot:level>
            <span class="ss-announcement-emphasized">{{
              $t("SV.LIT_LyricsInSongSelect")
            }}</span>
          </template>
        </i18n-t>
      </Alert>
    </template>
  </div>
</template>

<style scoped lang="postcss">
#above-banner-messages {
  @apply mb-2 grid gap-2;
}

.ss-announcement-emphasized {
  @apply font-semibold text-primary;
}

:deep() {
  .btn-variant-transparent-white {
    @apply -my-1;
  }
}
</style>
